/**
 * The feature-panel route set (ticket ui#150), using React-free route helpers so both the App shell
 * ({@link ./App}) and the individual feature routes share ONE source of truth for "which routes render
 * as the non-modal right FeaturePanel over the persistent home canvas", and the predicate can be
 * unit-tested without dragging in React/Firebase.
 *
 * Design/crash note (supersedes 72598f7's `canvasRoutes`): that reverted attempt moved an ALREADY
 * hydrated HomePage between route owners, which detached nodes React still owned. The current shell
 * never re-parents a mounted tree: HomePage has the same App-owned position in both the server render
 * and the first client render, then stays at that position while "/" opens a client-only destination.
 * This feature set still excludes "/" because home never opens a panel, not because it lacks the canvas.
 */

import {isAdminRoute} from "account/admin/adminRoute";
import {treeRelativePath} from "seo/documentLang";

/**
 * True for the index of EVERY tree this build emits: `/`, `/ro`, `/de` and their directory URLs.
 *
 * `App` renders the visible HomePage only when this says yes ({@link ./App} `showHomeCanvas`) — the
 * route record for a home path contributes metadata and nothing else — so a mirror this answers false
 * for is pre-rendered with the nav and no landing at all. That is what kitecorp/transferify-ui#2501
 * was: the test read `pathname.startsWith("/ro")` beside the tree-relative one, which excluded every
 * market past the second, and `build/de/index.html` shipped at 30,911 bytes with nothing under the
 * header. No error was logged and no boundary fired, because nothing threw — the landing was simply
 * never asked for.
 *
 * Asking {@link treeRelativePath} alone is the whole rule: it collapses `/ro` and `/de` onto `/`
 * using the shipped-locale list the build and the SEO layer already share, so the next market is
 * recognised the day its subtree exists. It also keeps answering false for a mirror's non-home pages
 * (`/de/pricing` is `/pricing`), which is what the prefix test was there to protect.
 */
export function isHomeRoute(pathname: string): boolean {
	return treeRelativePath(pathname) === "/";
}

/** Which feature sheet a route opens — the locale-independent identity that NAMES the sheet. */
export type FeatureSheetId = "transfers" | "contacts" | "account";

/**
 * The private routes that render INSIDE the FeaturePanel over the home canvas (never "/"), each with the
 * sheet it opens. One table for both questions below, so "does this route dock" and "which sheet is it"
 * cannot come to disagree: a route the shell docks but cannot name would render a sheet with no
 * accessible name, which is the failure `e2e/dialog-a11y.spec.ts` exists to catch.
 *
 * `/reminders` left this list in kitecorp/transferify-ui#3140: file requests became the Requested tab of
 * Transfers (`/transfers/requested`, which docks here through the `/transfers` prefix), and `/reminders`
 * survives only as a redirect to that tab, so old links keep working without a sheet of their own.
 */
const SHEET_ROUTES: ReadonlyArray<{id: FeatureSheetId; route: string}> = [
	{id: "transfers", route: "/transfers"},
	{id: "contacts", route: "/contacts"},
	{id: "account", route: "/account"},
];

/** The docked routes' paths, in {@link SHEET_ROUTES} order. */
export const FEATURE_PANEL_ROUTES: readonly string[] = SHEET_ROUTES.map(({route}) => route);

/**
 * The {@link SHEET_ROUTES} entry `pathname` docks, read inside the language tree ({@link treeRelativePath}):
 * the storefront subfolders serve the same private app, so `/de/account` docks the Account panel in
 * German (kitecorp/transferify-ui#3521).
 *
 * Matched by SEGMENT PREFIX, not by equality, so a nested route renders in the same panel as its parent
 * (`/transfers/requested`). The `"/"` in the prefix test is important: a bare `startsWith` would also
 * claim a sibling route whose name merely begins with a panel route's — `/accountability` is not
 * `/account`, and a shell that thought otherwise would mount the panel over an unrelated page.
 *
 * The admin console is the one nested path that does NOT dock in its parent's panel: it moved out of the
 * Account sheet into full pages under `/account/admin` (kitecorp/transferify-ui#3830), so it is excluded
 * by its own route rule before the table is read, rather than by a second copy of its path here.
 */
function dockedSheetRoute(pathname: string): {id: FeatureSheetId; route: string} | undefined {
	if (isAdminRoute(pathname)) {
		return undefined;
	}
	const path = treeRelativePath(pathname);
	return SHEET_ROUTES.find(({route}) => path === route || path.startsWith(`${route}/`));
}

/**
 * True when `pathname` is a route that renders as the docked FeaturePanel. FALSE for "/", every public
 * pre-rendered page and the admin console, so only the client-only destinations add the panel over the
 * persistent canvas.
 */
export function isFeaturePanelRoute(pathname: string): boolean {
	return dockedSheetRoute(pathname) !== undefined;
}

/**
 * The sheet `pathname` opens, or `null` when it opens none (the home canvas, every public page and the
 * admin console's full pages).
 *
 * React-free on purpose (epic #26): this is a route rule, not a component concern. The COPY that titles
 * each sheet is looked up from the catalog by the React layer, so the rule holds no translatable text.
 */
export function featureSheetIdFor(pathname: string): FeatureSheetId | null {
	return dockedSheetRoute(pathname)?.id ?? null;
}
